import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronsLeftIcon, ChevronsRightIcon, FileIcon, MailIcon, MessageCircleIcon, PaperclipIcon, PhoneIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { addNote, assignLead, deleteNote, updateLeadStage, updateNoteColor } from '../../services/leadService';
import { NoteColor } from '../../types';
import { NOTE_COLORS, noteColor } from './noteColors';
import { addFile, deleteFile } from '../../services/fileService';
import { answerToText, formatDateTime, fullName } from '../../utils/format';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { Avatar } from '../ui/Avatar';
import { SourceBadge } from './SourceBadge';
import { StageBadge } from './StageBadge';
import { NoteDialog } from './NoteDialog';
interface LeadDrawerProps {
  leadId: string | null;
  onClose: () => void;
}
type PanelState = 'compacto' | 'ampliado';

/** Sección plegable dentro de la ficha del lead. */
function Section({
  title,
  count,
  open,
  onToggle,
  children
}: {
  title: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return <section className="rounded-xl border border-border" data-id="element-104" data-mp="30w7vbvgpy83j">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left" data-id="element-105" data-mp="30vprodrq8rll">
        <span className="text-sm font-semibold text-foreground" data-id="element-106" data-mp="287apirtp23ro">
          {title}
          {count !== undefined && <span className="ml-2 rounded-full bg-surface px-2 py-0.5 text-xs font-medium text-muted-foreground" data-id="element-107" data-mp="2d6575qfxdma7">
              {count}
            </span>}
        </span>
        <ChevronDownIcon className={`h-4 w-4 flex-none text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-108" data-mp="2hz6p67bfi1y0" />
      </button>
      {open && <div className="border-t border-border p-4" data-id="element-109" data-mp="1pfhvqv6sweiw">{children}</div>}
    </section>;
}

/**
 * Panel lateral del lead con tres estados: cerrado, compacto y ampliado.
 * Las secciones son plegables y su estado se mantiene durante la sesión.
 */
export function LeadDrawer({
  leadId,
  onClose
}: LeadDrawerProps) {
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    leads,
    stages,
    sources,
    notes,
    history,
    forms,
    landings,
    files
  } = useWorkspace();
  const fileRef = useRef<HTMLInputElement>(null);
  const [panel, setPanel] = useState<PanelState>('compacto');
  const [essentials, setEssentials] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({
    info: true,
    respuestas: false,
    notas: true,
    historial: false,
    archivos: false
  });
  const [noteFeedback, setNoteFeedback] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [noteFilter, setNoteFilter] = useState<'all' | NoteColor>('all');
  const [noteDialog, setNoteDialog] = useState(false);
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    if (leadId) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [leadId, onClose]);
  const lead = leads.find(l => l.id === leadId);
  if (!lead) return null;
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const stage = stages.find(s => s.id === lead.stageId);
  const leadNotes = notes.filter(n => n.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const leadHistory = history.filter(h => h.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const visibleNotes = noteFilter === 'all' ? leadNotes : leadNotes.filter(n => (n.color ?? 'gray') === noteFilter);
  const leadFiles = files.filter(f => f.leadId === lead.id);
  const canEdit = hasPermission('leads.edit');
  const expanded = panel === 'ampliado';
  const essentialInfo: [string, string][] = [['Empresa', lead.company || '—'], ['Teléfono', lead.phone], ['Tipo de servicio', lead.service || '—'], ['Presupuesto disponible', lead.budget || '—']];
  const fullInfo: [string, string][] = [...essentialInfo, ['Email', lead.email], ['Instagram', lead.instagram || '—'], ['Ciudad', [lead.city, lead.country].filter(Boolean).join(', ') || '—'], ['Web', lead.website || '—'], ['Inversión actual en publicidad', lead.adBudget || '—'], ['Campaña', lead.origin.campaign || '—'], ['Landing', landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'], ['Formulario', forms.find(f => f.id === lead.origin.formId)?.name ?? '—'], ['Fecha de entrada', formatDateTime(lead.createdAt)], ['Último contacto', lead.lastContactAt ? formatDateTime(lead.lastContactAt) : 'Sin contactar']];
  const info = essentials ? essentialInfo : fullInfo;
  async function saveNote(note: {
    title?: string;
    body: string;
    color: NoteColor;
  }) {
    if (!lead) return;
    setNoteFeedback(null);
    await addNote(lead.organizationId, lead.id, user?.id ?? 'user', authorName, note.body, note.title, note.color);
    setNoteFeedback({
      ok: true,
      text: 'Nota guardada.'
    });
  }
  function toggle(key: string) {
    setOpen(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }
  const phoneDigits = lead.phone.replace(/\D/g, '');
  return <>
      <div className="fixed inset-0 z-40 bg-navy/25 backdrop-blur-[2px] lg:bg-navy/10" onClick={onClose} aria-hidden="true" data-id="element-110" data-mp="17dspe33yo4nk" />
      <aside role="dialog" aria-label={`Ficha de ${fullName(lead.firstName, lead.lastName)}`} className={`fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-background shadow-2xl transition-[max-width] duration-300 ${expanded ? 'lg:max-w-[680px]' : 'lg:max-w-[440px]'}`} data-id="element-111" data-mp="1cbkkf5zwk6vz">
        {/* Cabecera */}
        <header className="flex items-start gap-3 border-b border-border p-4 sm:p-5" data-id="element-112" data-mp="1q5lidxj15pva">
          <div className="min-w-0 flex-1" data-id="element-113" data-mp="1v3vi8lfp9en8">
            <p className="truncate text-base font-semibold text-navy" data-id="element-114" data-mp="3r68akga07f28">
              {fullName(lead.firstName, lead.lastName)}
            </p>
            <p className="truncate text-sm text-muted-foreground" data-id="element-115" data-mp="3n5odfcpfkbg3">{lead.company}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-116" data-mp="4qxco74phslu">
              <StageBadge stage={stage} stages={canEdit ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-117" data-mp="3w5bhy7dghxdd" />
              <SourceBadge source={lead.origin.source} sources={sources} data-id="element-118" data-mp="ensa0mp8tmyj" />
            </div>
          </div>
          <div className="flex flex-none items-center gap-1" data-id="element-119" data-mp="9op6j6owvoso">
            <button type="button" onClick={() => setPanel(expanded ? 'compacto' : 'ampliado')} aria-label={expanded ? 'Contraer panel' : 'Ampliar panel'} className="hidden rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground lg:block" data-id="element-120" data-mp="np9nryf6hck2">
              {expanded ? <ChevronsRightIcon className="h-4 w-4" data-id="element-121" data-mp="2piz6qpjs9anw" /> : <ChevronsLeftIcon className="h-4 w-4" data-id="element-122" data-mp="2kibk0g2l7fzh" />}
            </button>
            <button type="button" onClick={onClose} aria-label="Cerrar panel" className="rounded-lg p-2 text-muted-foreground hover:bg-surface hover:text-foreground" data-id="element-123" data-mp="zzr5ekda2qkp">
              <XIcon className="h-4 w-4" data-id="element-124" data-mp="14y2eutznuz2e" />
            </button>
          </div>
        </header>

        {/* Acciones rápidas */}
        <div className="grid grid-cols-3 gap-2 border-b border-border p-4 sm:p-5" data-id="element-125" data-mp="v912ddsbqzt6">
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<MessageCircleIcon className="h-4 w-4" data-id="element-127" data-mp="h7om11994w6r" />} onClick={() => window.open(`https://wa.me/${phoneDigits}`, '_blank', 'noopener')} data-id="element-126" data-mp="v6y4y8oue4ef">
            WhatsApp
          </Button>
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<MailIcon className="h-4 w-4" data-id="element-129" data-mp="c9wvaakcfg45" />} onClick={() => window.open(`mailto:${lead.email}`)} data-id="element-128" data-mp="89cvez8dxyeb">
            Email
          </Button>
          <Button variant="secondary" size="sm" className="min-h-[44px]" leftIcon={<PhoneIcon className="h-4 w-4" data-id="element-131" data-mp="26nxw30zged6v" />} onClick={() => window.open(`tel:${lead.phone}`)} data-id="element-130" data-mp="22nnojjw0jepw">
            Llamar
          </Button>
        </div>

        {/* Contenido */}
        <div className="flex-1 space-y-3 overflow-y-auto p-4 sm:p-5" data-id="element-132" data-mp="ugoxaj8l2dx1">
          <div className="flex items-center justify-between gap-3 rounded-xl bg-surface px-4 py-3" data-id="element-133" data-mp="pg9jjqke34ej">
            <Switch id="essentials" label="Mostrar solo lo esencial" checked={essentials} onChange={setEssentials} data-id="element-134" data-mp="knggd74kk44k" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2" data-id="element-135" data-mp="bocvpypr7gny">
            <Select id="drawer-stage" label="Estado" disabled={!canEdit} options={stages.map(s => ({
            value: s.id,
            label: s.name
          }))} value={lead.stageId} onChange={e => updateLeadStage(lead.id, e.target.value, authorName)} data-id="element-136" data-mp="bp4g0ddwhryx" />
            <Select id="drawer-owner" label="Comercial" placeholder="Sin asignar" disabled={!canEdit} options={members.map(m => ({
            value: m.userId,
            label: fullName(m.firstName, m.lastName)
          }))} value={lead.assignedTo ?? ''} onChange={e => {
            const member = members.find(m => m.userId === e.target.value);
            assignLead(lead.id, e.target.value || undefined, member ? fullName(member.firstName, member.lastName) : '', authorName);
          }} data-id="element-137" data-mp="3swq3uu1ylwgc" />
          </div>

          <Section title="Información principal" open={open.info} onToggle={() => toggle('info')} data-id="element-138" data-mp="28e5sr8buzp6b">
            <dl className="divide-y divide-border" data-id="element-139" data-mp="2dds6divln7fn">
              {info.map(([label, value]) => <div key={label} className="flex items-start justify-between gap-4 py-2.5" data-id="element-140" data-mp="1gnj881ybg3rw">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground" data-id="element-141" data-mp="1kol9spfpgrx1">
                    {label}
                  </dt>
                  <dd className="max-w-[60%] break-words text-right text-sm text-foreground" data-id="element-142" data-mp="1pn6bktalvjnl">
                    {value}
                  </dd>
                </div>)}
            </dl>
          </Section>

          {!essentials && <Section title="Respuestas del formulario" count={lead.answers.length} open={open.respuestas} onToggle={() => toggle('respuestas')} data-id="element-143" data-mp="312uxrig1cenc">
              {lead.answers.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-144" data-mp="y3jywx4fsu5">
                  Este lead no tiene respuestas asociadas.
                </p> : <dl className="divide-y divide-border" data-id="element-145" data-mp="5wet15fldzbb">
                  {lead.answers.map(answer => <div key={answer.questionId} className="py-2.5" data-id="element-146" data-mp="aphnrgw3vet3">
                      <dt className="text-sm font-medium text-foreground" data-id="element-147" data-mp="eqt38tl9mjnk">
                        {answer.label}
                      </dt>
                      <dd className="mt-1 text-sm text-muted-foreground" data-id="element-148" data-mp="3e7izi14p19oj">
                        {answerToText(answer.value) || '—'}
                      </dd>
                    </div>)}
                </dl>}
            </Section>}

          <Section title="Notas" count={leadNotes.length} open={open.notas} onToggle={() => toggle('notas')} data-id="element-149" data-mp="3s5fzl7icvy7y">
            {hasPermission('notes.edit') && <div className="mb-4 space-y-2" data-id="element-150" data-mp="12f40lmrr6173">
                {/* La nota se escribe en una ventana flotante, más cómoda que
                    un campo estrecho dentro de la propia ficha. */}
                <Button size="sm" className="min-h-[44px] w-full" onClick={() => setNoteDialog(true)} data-id="element-151" data-mp="1hi4029wcsi03">
                  Escribir nota
                </Button>
                {noteFeedback && <p role="status" className={`text-sm ${noteFeedback.ok ? 'text-emerald-600' : 'text-red-600'}`} data-id="element-152" data-mp="17su8qw9c33d1">
                    {noteFeedback.text}
                  </p>}
              </div>}
            {/* Filtro por color */}
            <div className="mb-3 flex flex-wrap gap-1.5" data-id="element-153" data-mp="ytgy409h48qq">
              {([{
              value: 'all',
              label: 'Todas'
            }, ...NOTE_COLORS.map(c => ({
              value: c.value,
              label: `${c.label}s`
            }))] as {
              value: 'all' | NoteColor;
              label: string;
            }[]).map(option => <button key={option.value} type="button" aria-pressed={noteFilter === option.value} onClick={() => setNoteFilter(option.value)} className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${noteFilter === option.value ? 'bg-foreground text-background' : 'bg-surface text-muted-foreground hover:text-foreground'}`} data-id="element-154" data-mp="2j8iuxlerjycs">
                  {option.label}
                </button>)}
            </div>
            {visibleNotes.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-155" data-mp="3kvfj75n41zd">
                Todavía no hay notas ni comentarios.
              </p> : <ol className="space-y-3" data-id="element-156" data-mp="7lfbw8ie31kg">
                {visibleNotes.map(note => <li key={note.id} className={`rounded-xl border border-border border-l-4 p-3 ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-157" data-mp="vbt7ua43hkkb">
                    <div className="flex items-center gap-2" data-id="element-158" data-mp="ma24vd0xvru5">
                      <Avatar name={note.authorName} size="sm" data-id="element-159" data-mp="m9jwji105n5v" />
                      <div className="min-w-0 flex-1" data-id="element-160" data-mp="r8depnr8i2cd">
                        {note.title && <p className="truncate text-sm font-semibold text-foreground" data-id="element-161" data-mp="370apy9hxkdl1">
                            {note.title}
                          </p>}
                        <p className="text-xs text-muted-foreground" data-id="element-162" data-mp="3c0ynle0nzso8">
                          {note.authorName} · {formatDateTime(note.createdAt)}
                        </p>
                      </div>
                      {note.authorId === user?.id && hasPermission('notes.edit') && <button type="button" aria-label="Eliminar nota" onClick={() => deleteNote(note.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-163" data-mp="3dgsyut6z4hfs">
                          <Trash2Icon className="h-4 w-4" data-id="element-164" data-mp="2kr3xgeqcqfdn" />
                        </button>}
                    </div>
                    {/* Cambiar el color de una nota ya guardada */}
                    {hasPermission('notes.edit') && <div className="mt-2 flex gap-1.5" data-id="element-165" data-mp="2uiqdcodmx2mq">
                        {NOTE_COLORS.map(tone => <button key={tone.value} type="button" aria-label={`Marcar como ${tone.label}`} aria-pressed={note.color === tone.value} onClick={() => updateNoteColor(note.id, tone.value)} className={`h-6 w-6 rounded-full ${tone.dot} transition-transform hover:scale-110 ${note.color === tone.value ? 'ring-2 ring-foreground ring-offset-2' : 'opacity-50'}`} data-id="element-166" data-mp="2pqouukpvifo9" />)}
                      </div>}
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground" data-id="element-167" data-mp="1597mnd733hgu">
                      {note.body}
                    </p>
                  </li>)}
              </ol>}
          </Section>

          {!essentials && <Section title="Historial y actividad" count={leadHistory.length} open={open.historial} onToggle={() => toggle('historial')} data-id="element-168" data-mp="1e3vp6vl9yl1y">
              <ol className="space-y-3" data-id="element-169" data-mp="1o0i8vh1v762e">
                {leadHistory.map(event => <li key={event.id} className="flex gap-3" data-id="element-170" data-mp="1nynpw8a5lo0y">
                    <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-primary" data-id="element-171" data-mp="1szbll79but1i" />
                    <div data-id="element-172" data-mp="1x1oqjtg92mx8">
                      <p className="text-sm text-foreground" data-id="element-173" data-mp="1wzvo6qco6ov1">{event.description}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground" data-id="element-174" data-mp="3urzwm2s7qkru">
                        {event.authorName} · {formatDateTime(event.createdAt)}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </Section>}

          {!essentials && <Section title="Archivos" count={leadFiles.length} open={open.archivos} onToggle={() => toggle('archivos')} data-id="element-175" data-mp="3mhsfwf31t6z7">
              <Button variant="secondary" size="sm" className="mb-3 w-full" leftIcon={<PaperclipIcon className="h-4 w-4" data-id="element-177" data-mp="3di5qkrnlfrmi" />} onClick={() => fileRef.current?.click()} data-id="element-176" data-mp="3hk90vcyj0q67">
                Adjuntar archivo
              </Button>
              <input ref={fileRef} type="file" className="sr-only" onChange={e => {
            const file = e.target.files?.[0];
            if (file && lead) addFile(lead.organizationId, lead.id, file, authorName);
          }} data-id="element-178" data-mp="e1pkb7p7n8r8" />
              {leadFiles.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-179" data-mp="3iu8dqi6d24">
                  No hay archivos adjuntos.
                </p> : <ul className="space-y-2" data-id="element-180" data-mp="2pt2xmnnieccp">
                  {leadFiles.map(file => <li key={file.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2" data-id="element-181" data-mp="2kt892qr7hyut">
                      <FileIcon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden="true" data-id="element-182" data-mp="2m9uerdfdmfb2" />
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground" data-id="element-183" data-mp="2r9gtjraus3lz">
                        {file.name}
                      </span>
                      <button type="button" aria-label="Eliminar archivo" onClick={() => deleteFile(file.id)} className="rounded-md p-1.5 text-muted-foreground hover:text-red-600" data-id="element-184" data-mp="2dbs6cctbckdr">
                        <Trash2Icon className="h-4 w-4" data-id="element-185" data-mp="23ibehecqlfcq" />
                      </button>
                    </li>)}
                </ul>}
            </Section>}
        </div>
      </aside>

      <NoteDialog open={noteDialog} leadName={fullName(lead.firstName, lead.lastName)} onClose={() => setNoteDialog(false)} onSave={saveNote} data-id="element-186" data-mp="1tmrhspqcmcmx" />
    </>;
}